import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  credentialSetsForPlatform,
  type SocialAppKeysStatus,
  type SocialCredentialSet,
  type SocialPlatform,
} from '@mg/shared';
import { api } from '../../lib/api';
import { SOCIAL_APP_KEYS_STATUS_KEY } from './socialAppKeys';

/**
 * Whether a connection can start with the install's Site Integrations keys:
 * - `ready`: start it;
 * - `missing`: its set has no keys - say so, and do not start;
 * - `checking`: the status has not answered yet - wait, and say nothing.
 */
export type KeyState = 'ready' | 'missing' | 'checking';

/** The Connections page's gate, from one answer of the key status. */
export interface SocialAppKeysGate {
  /** The state of one set. A connection with no set (null) keeps its own app, so it is never gated. */
  stateOf(set: SocialCredentialSet | null): KeyState;
  /**
   * The state of a NEW connection on `platform`: missing only when EVERY set it could use is missing
   * (LinkedIn has two, one per connection type; its dialog gates the type chosen). A platform with
   * no set is never gated.
   */
  newConnectionState(platform: SocialPlatform): KeyState;
}

/**
 * The gate for one answer of the key status. Fails OPEN: the server refuses a start whose keys are
 * missing whatever the page shows, so a status that could not be read - an error before any answer
 * (an outage, an older API with no such route: 404), a malformed answer, a set it does not mention -
 * never blocks a connection. Only an answer that says `false` for a set makes it `missing`.
 *
 * An error fails open only while nothing has been answered yet. A refetch that fails keeps the last
 * answer until one succeeds (React Query keeps its data and still reports `isError`): a set it said
 * is missing stays `missing`, so a passing outage does not re-enable what the server would refuse,
 * and every other set stays `ready`.
 */
export function gateFrom(data: SocialAppKeysStatus | undefined, failed: boolean): SocialAppKeysGate {
  const stateOf = (set: SocialCredentialSet | null): KeyState => {
    if (set === null) return 'ready';
    if (failed && data === undefined) return 'ready';
    if (data === undefined) return 'checking';
    const answer: unknown = typeof data === 'object' && data !== null ? (data as Record<string, unknown>)[set] : undefined;
    return answer === false ? 'missing' : 'ready';
  };
  return {
    stateOf,
    newConnectionState(platform) {
      const states = credentialSetsForPlatform(platform).map(stateOf);
      if (states.length === 0) return 'ready';
      if (states.includes('checking')) return 'checking';
      return states.every((state) => state === 'missing') ? 'missing' : 'ready';
    },
  };
}

/**
 * The key status as the Connections page's gate. One observer per page: the dialogs take the gate
 * as a prop, so opening one asks nothing.
 *
 * Asked again whenever the window regains focus (App.tsx turns that off for every other query): a
 * member's open tab must see keys an admin has saved since, without a reload.
 */
export function useSocialAppKeysStatus(): SocialAppKeysGate {
  const { data, isError } = useQuery({
    queryKey: SOCIAL_APP_KEYS_STATUS_KEY,
    queryFn: ({ signal }) => api.getSocialAppKeysStatus(signal),
    refetchOnWindowFocus: true,
  });
  return useMemo(() => gateFrom(data, isError), [data, isError]);
}
